import { useEffect } from 'react'
// @ts-expect-error TS7016
import md5 from 'md5'

import { useDeepEffect, useSetState } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'

import type { SelectedOption } from '@functions/selectPriceListItemOptionValue'

import type { AgencyModel, CampaignModel, ProviderPriceListItemModel } from '@models/types'

const defaultState = {
  currentHash: '',
  results: {},
}

type UseAgencyPriceOptions = {
  agency?: AgencyModel,
  campaign?: CampaignModel,
  performHttpRequests?: boolean,
  providerPriceListItem: ProviderPriceListItemModel,
  selectedProviderPriceListItemOptions: SelectedOption[],
}

function useAgencyPrice(options: UseAgencyPriceOptions) {
  const {
    agency,
    campaign,
    performHttpRequests = true,
    providerPriceListItem,
    selectedProviderPriceListItemOptions,
  } = options

  const [state, setState] = useSetState(defaultState)
  const { results } = state

  // Store the prices in a hash with the MD5 as the key
  // Use the key to retrieve the current price.
  // We use the same key on the server side and can then match up to ensure
  // the price displayed is correct.
  const itemHash = providerPriceListItem.id
    ? md5(JSON.stringify(`${providerPriceListItem.id}-${selectedProviderPriceListItemOptions}`))
    : ''

  useEffect(() => {
    setState({
      currentHash: itemHash,
    })
  }, [itemHash])

  // Load Agency Price
  const loadAgencyPricePayload = useReduxAction(
    'providerPriceListItems',
    'loadAgencyPrice',
    {},
    [performHttpRequests, providerPriceListItem.id, JSON.stringify(selectedProviderPriceListItemOptions)],
    {
      dispatchAction: (action, requestOptions) => action(providerPriceListItem.id, {
        ...requestOptions,
        agency_id: agency?.id,
        campaign_id: campaign?.id,
        item_options: JSON.stringify(selectedProviderPriceListItemOptions),
      }),
      shouldPerformFn: () => performHttpRequests && !!providerPriceListItem.id,
    },
  )

  const { data, loading, success } = loadAgencyPricePayload

  useDeepEffect(() => {
    // Check if returned price matches current selected item
    // App could fire off 2 requests at the same time and if they dont arrive in the correct
    // order the price will be incorrect.
    if (itemHash){
      setState({
        results: {
          ...results,
          [itemHash]: {
            data,
            success,
            // @ts-expect-error TS2339
            total: success && data ? data.price : 0,
          },
        },
      })
    }
  }, [data, success])

  return {
    // @ts-expect-error TS7053
    currentPrice: results[itemHash] || { total: 0 },
    loading,
    success,
    ...state,
  }
}

export default useAgencyPrice
